import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, { getApiErrorMessage } from '@/Services/api';
import Button from '@/components/ui/Button';
import { cartLabels } from '@/data/content';

interface OrderInfo {
    order_number: string;
    pickup_code: string | null;
    service_date: string | null;
    delivery_time: string | null;
}

interface SuccessData {
    orders: OrderInfo[];
    payment_method: string;
}

const OrderSuccessPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');

    const [successData, setSuccessData] = useState<SuccessData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!sessionId) {
            setError(cartLabels.session_not_found);
            setLoading(false);
            return;
        }

        const verifySession = async () => {
            try {
                const response = await api.post('/api/checkout/verify-session', {
                    session_id: sessionId,
                });
                setSuccessData(response.data);
            } catch (err: unknown) {
                const message = getApiErrorMessage(err, cartLabels.verify_error_fallback);
                setError(message);
            } finally {
                setLoading(false);
            }
        };

        verifySession();
    }, [sessionId]);

    // ── Loading state ──
    if (loading) {
        return (
            <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-app-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-app-muted">{cartLabels.verifying_payment}</p>
                </div>
            </div>
        );
    }

    // ── Error state ──
    if (error) {
        return (
            <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-app-surface border border-app-border rounded-2xl p-8 text-center shadow-sm">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                            className="w-8 h-8 text-red-600 dark:text-red-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-black text-app-text mb-2">{cartLabels.payment_not_confirmed}</h2>
                    <p className="text-app-muted mb-6 leading-relaxed">{error}</p>
                    <Button variant="primary" onClick={() => navigate('/checkout', { replace: true })} fullWidth>
                        {cartLabels.back_to_checkout}
                    </Button>
                </div>
            </div>
        );
    }

    // ── Success state ──
    const orders = successData?.orders ?? [];
    return (
        <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-app-surface border border-app-border rounded-2xl p-8 text-center shadow-sm">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                        className="w-8 h-8 text-green-600 dark:text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h2 className="text-2xl font-black text-app-text mb-2">{cartLabels.success_title}</h2>
                <p className="text-app-muted mb-6 leading-relaxed">
                    {orders.length === 1 ? (
                        <>
                            {cartLabels.success_single.replace('{number}', orders[0].order_number)}
                            {successData?.payment_method === 'online' && cartLabels.payment_accepted}.
                        </>
                    ) : (
                        <>
                            {cartLabels.success_multiple.replace('{count}', String(orders.length))}
                            {successData?.payment_method === 'online' && cartLabels.payment_accepted}.
                        </>
                    )}
                </p>

                <div className="space-y-3 mb-6">
                    {orders.map((order) => (
                        <div key={order.order_number} className="bg-app-bg border border-app-border rounded-xl p-4">
                            <p className="text-xs font-semibold text-app-muted mb-1">
                                {order.order_number}
                                {order.service_date && (
                                    <> — {new Date(order.service_date + 'T00:00:00').toLocaleDateString('fr-FR')}</>
                                )}
                                {order.delivery_time && <> à {order.delivery_time}</>}
                            </p>
                            {order.pickup_code && (
                                <p className="text-3xl font-black text-app-primary tracking-widest">
                                    {order.pickup_code}
                                </p>
                            )}
                        </div>
                    ))}
                </div>

                <Button variant="primary" onClick={() => navigate('/', { replace: true })} fullWidth>
                    {cartLabels.success_back}
                </Button>
            </div>
        </div>
    );
};

export default OrderSuccessPage;
